import { useUserStore } from "@/stores/user";
import { unifiedApi } from "@/common";
import { IClientException } from "./api/unifiedPortal/interfaces";
import { BUILD_HASH, BUILD_HASH_DISPLAY_LENGTH, FLAGS } from "@/define";
import { AxiosError } from "axios";
import { IErrorMessage } from "./api/interfaces";

const EVENT_ARRAY = "event_array";
const MAX_EVENTS = 10;
const CLIENT_IDENTIFIER = "VueDawn";

interface eventData {
  ticks: number;
}

function getEvents(): eventData[] {
  const eventsString = localStorage.getItem(EVENT_ARRAY);
  if (!eventsString) return [];

  const parsed: unknown = JSON.parse(eventsString);
  const events = Array.isArray(parsed)
    ? parsed.flatMap((event): eventData[] => {
        if (typeof event !== "object" || event === null || !("ticks" in event) || typeof event.ticks !== "number") {
          return [];
        }
        return [{ ticks: event.ticks }];
      })
    : [];

  const serializedEvents = JSON.stringify(events);
  if (serializedEvents !== eventsString) {
    localStorage.setItem(EVENT_ARRAY, serializedEvents);
  }
  return events;
}

export function logEvent(message: string, file?: string | undefined, value?: unknown) {
  // SECURITY: Caller-controlled log data can contain credentials or personal information.
  void message;
  void file;
  void value;
  const ev: eventData = {
    ticks: new Date().getTime(),
  };

  const events = getEvents();

  //add the new event
  events.push(ev);
  if (events.length > MAX_EVENTS) events.shift();

  //save the array
  localStorage.setItem(EVENT_ARRAY, JSON.stringify(events));

  //now log to the console
  if (FLAGS.IS_DEBUG) {
    console.log(new Date(ev.ticks).toISOString());
  }
}

interface IErrorObject {
  time: string;
  message: string;
  status: number | undefined;
  pageURL: string;
  apiURL: string;
}

export async function logAxiosException(err: AxiosError) {
  const userStore = useUserStore();
  const user = userStore.currentUser;

  //Build a error object to stringify
  const errorObject: IErrorObject = {
    time: new Date().getTime().toString(),
    message: (err.response?.data as IErrorMessage).message ?? "",
    status: err.response?.status,
    pageURL: window.location.href,
    apiURL: err.request.responseURL ?? "",
  };

  let stackTrace = "";
  if (err.stack) stackTrace = JSON.stringify(err.stack.split("\n"));

  //create clientException message
  const ce: IClientException = {
    clientIdentifier: `${CLIENT_IDENTIFIER} ${BUILD_HASH.substring(0, BUILD_HASH_DISPLAY_LENGTH)}`,
    errorMessage: JSON.stringify(errorObject),
    cause: JSON.stringify(getEvents()),
    stackTrace,
    url: err.request.responseURL ?? "",
    userAgent: "isMobile: " + !localStorage.getItem("isMobile") + " OS: " + localStorage.getItem("getOS"),
    ipAddress: "",
    personId: user?.personId || 0,
    resellerId: user?.subscriberInfo?.managingResellerID || 0,
  };

  if (FLAGS.IS_DEBUG) {
    console.log(`${new Date().toISOString()} | ${ce.personId} | ${ce.resellerId}`);
    console.log(ce.stackTrace);
    console.log(ce.userAgent);
    console.log(JSON.stringify(ce));
  }

  //send it
  await unifiedApi.putClientException(ce);

  //delete the queue of events
  localStorage.removeItem(EVENT_ARRAY);
}

export async function logException(err: Error, extra?: unknown) {
  //check if error is an Axios error and use logAxiosException if it is
  const axiosError = err as AxiosError;
  if (axiosError?.request?.responseURL) {
    logAxiosException(axiosError);
    return;
  }

  const userStore = useUserStore();
  const user = userStore.currentUser;

  const errorMessage = new Date().getTime().toString();
  let extraString = "";
  if (extra) extraString = JSON.stringify(extra);

  let stackTrace = "";
  if (err.stack) stackTrace = JSON.stringify(err.stack.split("\n"));

  //create clientException message
  const ce: IClientException = {
    clientIdentifier: `${CLIENT_IDENTIFIER} ${BUILD_HASH.substring(0, BUILD_HASH_DISPLAY_LENGTH)}`,
    errorMessage,
    cause: JSON.stringify(getEvents()),
    stackTrace,
    url: extraString,
    userAgent: "isMobile: " + !localStorage.getItem("isMobile") + " OS: " + localStorage.getItem("getOS"),
    ipAddress: "",
    personId: user?.personId || 0,
    resellerId: user?.subscriberInfo?.managingResellerID || 0,
  };

  if (FLAGS.IS_DEBUG) {
    console.log(`${new Date().toISOString()} | ${ce.personId} | ${ce.resellerId}`);
    console.log(ce.stackTrace);
    console.log(ce.userAgent);
    if (extra) console.log(extra);
  }

  //send it
  await unifiedApi.putClientException(ce);

  //delete the queue of events
  localStorage.removeItem(EVENT_ARRAY);
}
